<template>
    <div>

        <vue-headful title="User Management :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-user"></i> Users</li>
                <li class="ml-3">
                    <router-link :to="`/users/create`" class="btn btn-sm btn-success"><i class="fa fa-plus-circle"></i> Create</router-link>
                </li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none" v-if="canManage">
            <b-nav-item :active="curTab === 'users'" @click="curTab = 'users'">
                Users ({{ user_count }})
            </b-nav-item>
            <b-nav-item :active="curTab === 'roles'" @click="curTab = 'roles'">
                Roles ({{ roles.length }})
            </b-nav-item>
            <b-nav-item :active="curTab === 'role-requests'" @click="curTab = 'role-requests'">
                Role Requests ({{ requests.length }})
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block" v-if="canManage">
            <b-dropdown :html="getTabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === 'users'" @click="curTab = 'users'">
                    Users ({{ user_count }})
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'roles'" @click="curTab = 'roles'">
                    Roles ({{ roles.length }})
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'role-requests'" @click="curTab = 'role-requests'">
                    Role Requests ({{ requests.length }})
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="card" v-show="curTab === 'users'">
            <div class="card-body">
                <user-list ref="userIndexList" @userListCount="setUserCount" />
            </div>
        </div>

        <template v-if="canManage">
            <div class="card" v-show="curTab == 'roles'">
                <div class="card-body">
                    <v-client-table ref="RolesTable" :data="roles" :columns="roleColumns" :options="roleOptions">

                        <div slot="afterFilter" class="ml-2">
                            <router-link class="btn btn-outline-success" :to="`/users/roles/create`">
                                <i class="fa fa-plus"></i> Add
                            </router-link>
                        </div>

                        <template slot="name" slot-scope="props" title="Name">
                            <router-link :to="`/users/roles/edit/${props.row.id}`">
                                {{ props.row.name }}
                            </router-link>
                        </template>
                        <template slot="is_active" slot-scope="props" title="Active">
                            <span class="badge badge-success" v-if="props.row.is_active">Active</span>
                            <span class="badge badge-warning" v-else>Inactive</span>
                        </template>

                        <template slot="child_row" slot-scope="props">
                            <div><strong>Permissions:</strong></div>
                            <ul>
                                <li v-for="perm in props.row.permissions">{{ perm.name }}: {{ perm.description }}</li>
                            </ul>
                        </template>
                    </v-client-table>
                </div>
            </div>
            <div class="card" v-show="curTab == 'role-requests'">
                <div class="card-body">
                    <div class="form-inline">
                        <label class="mr-1">User:</label>
                        <b-form-select v-model="filter_by_user_id" :options="filterUsers" size="sm" @change="filterRequests"></b-form-select>
                        <b-button variant="light" size="sm" v-on:click="filter_by_user_id = null" class="ml-1" v-if="filter_by_user_id">
                            <i class="fa fa-remove"></i> Clear
                        </b-button>
                    </div>
                    <hr/>

                    <div class="alert alert-danger mt-2 mb-2" v-if="errors.request_billing_error">
                        <h4 class="text-danger">User Licenses Exceeded</h4>
                        <div class="mb-1" v-for="error in errors.request_billing_error">{{ error }}</div>
                        <p>To upgrade your user licenses, an admin can visit the <router-link to="/billing">Account Billing Settings</router-link></p>
                    </div>

                    <b-table striped hover bordered small :items="filteredRequests" :fields="requestFields" show-empty>
                        <template v-slot:cell(id)="data">
                            <b-button variant="success" size="xs" v-on:click="takeAction(data.item.id, 'Approve')">
                                <i class="fa fa-check"></i> Approve
                            </b-button>
                            <b-button variant="danger" size="xs" v-on:click="takeAction(data.item.id, 'Deny')">
                                <i class="fa fa-remove"></i> Deny
                            </b-button>
                        </template>
                        <template v-slot:cell(FirstName)="data">
                            {{ data.item.FirstName }} {{ data.item.LastName }}
                        </template>
                        <template slot="empty" slot-scope="scope">
                            <strong>{{ scope.emptyText }}</strong>
                        </template>
                    </b-table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                getDeleted: false,
                getDev: false,
                curTab: 'users',
                user_count: null,

                loading: true,
                saving: false,
                errors: {},
                requests: [],
                requestFields: [
                    {key: 'id', label: 'Actions'},
                    {key: 'name', label: 'Role'},
                    {key: 'FirstName', label: 'User'},
                ],
                users: [],
                roles: [],
                filter_by_user_id: null,

                roleColumns: ['name', 'description', 'is_active'],
                roleOptions: {
                    columnsDropdown: false,
                    headings: {
                        name: 'Name',
                        description: 'Description',
                        is_active: 'Active',
                    }
                },
            }
        },

        computed: {

            canManage(){
                //Send it
                return this.$store.state.user.is_admin;
            },

            filterUsers(){
                //Setup
                var users = [
                    {value: null, text: 'Select a User to filter Requests'}
                ];

                //Loop
                for(var user_id in this.users)
                {
                    //Append
                    users.push({value: this.users[user_id].user_id, text: this.users[user_id].name})
                }

                return users;
            },

            filteredRequests(){
                //Check for null
                if(!this.filter_by_user_id)
                {
                    return this.requests;
                }

                //Loop and build
                var requests = [];

                //Loop
                for(var i in this.requests)
                {
                    //Check matching ID
                    if(this.requests[i].requester_id == this.filter_by_user_id)
                    {
                        requests.push(this.requests[i]);
                    }
                }

                //Send it
                return requests;
            },

            getTabTitle(){
                //Setup
                var title = '';
                var icon = 'bars';

                //Switch
                switch(this.curTab)
                {
                    case 'users':
                        //Set
                        title = 'Users ('+this.user_count+')';
                        break;
                    case 'roles':
                        //Set
                        title = 'Roles ('+this.roles.length+')';
                        break;
                    case 'role-requests':
                        //Set
                        title = 'Requests ('+this.requests.length+')';
                        break;
                }

                //Send it
                return '<i class="fa fa-'+icon+'"></i> ' + title;
            }

        },

        methods: {
            //Get the Various Resources We Need to Edit an User
            getErrorClass(attribute) {
                return {
                    'form-group has-error': this.errors[attribute],
                    'form-group': !this.errors[attribute],
                }
            },

            getRequests() {
                //Get the data
                this.$http
                    .get('admin/role-requests')
                    .then(response => {
                        //Set all the data
                        this.requests = response.data.requests;
                        this.users = response.data.users;

                        //Set loading
                        this.loading = false;

                        //Check the route
                        if(this.$route.query.user_id && this.users[this.$route.query.user_id])
                        {
                            this.filter_by_user_id = this.$route.query.user_id;
                        }
                    })
                    .catch(response => {

                    });
            },

            takeAction(id, action) {
                //Basic Confirmation Right now
                if(!confirm('Are you sure you want to '+action+' this request?'))
                {
                    return;
                }

                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Send it
                this.$http
                .post('admin/role-requests/'+id, {action: action})
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Role '+action);

                    //Splice it from the list?
                    this.updateRequests(id);

                    //Return to main list?
                    //this.$router.push('/');
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data;
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            updateRequests(id, deleteMode = true){
                //We need to check to see if the bid is new, or updated
                for (var i = this.requests.length - 1; i >= 0; i--)
                {
                    //If the IDs match, replace it
                    if(this.requests[i].id == id)
                    {
                        //Check if we are deleting it
                        if(typeof deleteMode != 'undefined' && deleteMode == true)
                        {
                            this.requests.splice(i, 1);
                            return;
                        }

                        //Set the value
                        this.requests.splice(i, 1, action);
                        //this.$refs.correctiveActionModal.hideModal();

                        //Stop here;
                        return;
                    }
                }
            },

            filterRequests(user_id){
                //set it
                this.filter_by_user_id = user_id;
            },

            setUserCount(count){
                //Set the count
                this.user_count = count;
            },

            getRolesAndPermissions() {
                //Get the data
                this.$http
                    .get('admin/roles')
                    .then(response => {
                        //Set all the data
                        this.roles = response.data.data;
                    })
                    .catch(response => {

                    });
            },
        },

        mounted(){
            //Send it
            if(this.canManage)
            {
                //Call the Data Getters
                this.getRolesAndPermissions();

                //Setup
                this.getRequests();

                //Check for a hash
                if(window.location.hash)
                {
                    //Set the current tab
                    this.curTab = window.location.hash.replace('#', '');
                }
            }

            //Focus on the SEarch Input
            this.$refs.userIndexList.$refs.userTable.$refs.genericFilter.focus();
        },
    }
</script>
